import type { TOptions } from 'i18next'

/**
 * Crash game types - games where the outcome is a multiplier crash point
 * rather than marble/option selection results
 */
const CRASH_GAME_TYPES = ['crash'] as const

/**
 * Check if a game type is a crash-style game (Aviator/Bustabit model)
 * These games show a multiplier crash point instead of marble results
 */
export const isCrashGameType = (gameType: string): boolean => {
  return CRASH_GAME_TYPES.includes(gameType as (typeof CRASH_GAME_TYPES)[number])
}

/**
 * CCTV game types — vehicle/object counter games rendered from a live
 * camera feed (irl-vehicle-counter engine). These games carry a
 * per-round numeric outcome (count) and have boundary-based bet
 * options (Under N / Range / Over N / Exact) instead of marble
 * selections. Drives the boundary-pill render in the chip-placement
 * panel and the location pill in betting history.
 *
 * `crash` is grouped here for one axis only — MUSIC SUPPRESSION. Like
 * the CCTV games, crash streams its own soundtrack inside the clip
 * audio, so the widget's background-music channel is force-muted for it
 * (see Music.tsx). Crash is NOT a boundary/counter game — its bet-option
 * decoration lives behind isCrashGameType, not this predicate.
 */
const CCTV_GAME_TYPES = [
  'irl',
  'irl_alt',
  'duck_river',
  'snow_run',
  'social_betting',
  'crash'
] as const

/**
 * Check if a game type is a CCTV-style game (vehicle/object counter
 * with boundary-based betting). Drives CCTV-only widget decoration.
 */
export const isCCTVGameType = (gameType: string): boolean => {
  return CCTV_GAME_TYPES.includes(gameType as (typeof CCTV_GAME_TYPES)[number])
}

/**
 * Birdie Time — pre-filmed golf putting with per-round dynamic odds. The
 * board is priced from `race_ready.payload.odds` (never from the game-level
 * config), auto-bet is hidden (the odds are new every round) and the result
 * view is the three putt circles. NOT a CCTV type (no boundary pills) and NOT
 * a crash type (no HUD, no multiplier climb).
 */
export const BIRDIE_GAME_TYPES = ['birdie_time'] as const

export const isBirdieGameType = (gameType?: string): boolean =>
  gameType !== undefined &&
  BIRDIE_GAME_TYPES.includes(gameType as (typeof BIRDIE_GAME_TYPES)[number])

const selectionTypeMap = (
  t: (key: string, options?: TOptions) => string
): Record<string, { singular: string; plural: string }> => ({
  stairpong: { singular: t('ball'), plural: t('balls') },
  fishtank: { singular: t('option'), plural: t('options') },
  marbles: { singular: t('marble'), plural: t('marbles') },
  marbles_survivor: { singular: t('marble'), plural: t('marbles') },
  ducks: { singular: t('duck'), plural: t('ducks') },
  coin_flip: { singular: t('coin'), plural: t('coins') },
  irl: { singular: t('option'), plural: t('options') },
  irl_alt: { singular: t('option'), plural: t('options') },
  duck_river: { singular: t('option'), plural: t('options') },
  snow_run: { singular: t('option'), plural: t('options') },
  social_betting: { singular: t('option'), plural: t('options') },
  crash: { singular: t('option'), plural: t('options') },
  birdie_time: { singular: t('option'), plural: t('options') },
  fast_lane: { singular: t('car'), plural: t('cars') }
})

export const getSelectionTypeOptions = (
  gameType: string,
  t: (key: string, options?: TOptions) => string
) => {
  const { singular, plural } = selectionTypeMap(t)[gameType] || {
    singular: t('item'),
    plural: t('items')
  }

  return {
    singular,
    plural
  }
}

export const getSelectionTypeSingular = (
  gameType: string,
  t: (key: string, options?: TOptions) => string
) => getSelectionTypeOptions(gameType, t).singular

export const getSelectionTypePlural = (
  gameType: string,
  t: (key: string, options?: TOptions) => string
) => getSelectionTypeOptions(gameType, t).plural
